import React from 'react';
import styled from 'styled-components';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 1.5rem;
  padding: 1rem 0;
  border-top: 1px solid #eee;
  
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 1rem;
  }
`;

const PaginationInfo = styled.div`
  color: #666;
  font-size: 0.9rem;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background: white;
  cursor: pointer;
  transition: transform 0.2s ease, background-color 0.2s ease, border-color 0.2s ease;
  will-change: transform, background-color;
  min-width: 36px;
  height: 36px;
  
  &:hover:not(:disabled) {
    background-color: #f8f9fa;
    border-color: var(--primary-green);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  &.active {
    background-color: var(--primary-green);
    border-color: var(--primary-green);
    color: white;
  }
`;

const PageInput = styled.input`
  width: 60px;
  padding: 0.25rem 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  
  &:focus {
    border-color: var(--primary-green);
    outline: none;
  }
`;

const Pagination = ({ 
  currentPage, 
  totalPages, 
  totalItems, 
  pageSize, 
  onPageChange 
}) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  const handlePageInputChange = (e) => {
    const page = parseInt(e.target.value);
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      onPageChange(page);
    }
  };

  const handlePageClick = (page) => {
    if (
      !onPageChange || 
      typeof page !== 'number' || 
      page < 1 || 
      page > totalPages || 
      page === currentPage
    ) {
      return;
    }
    onPageChange(page);
  };

  const getVisiblePages = () => {
    if (totalPages <= 6) {
      // If total pages is small, show all pages
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const delta = 2; // Number of pages to show on each side of current page
    const range = [];
    const rangeWithDots = [];

    // Calculate the range around current page
    const start = Math.max(2, currentPage - delta);
    const end = Math.min(totalPages - 1, currentPage + delta);

    // Always show first page
    rangeWithDots.push(1);

    // Add ellipsis after first page if needed
    if (start > 2) {
      rangeWithDots.push('...');
    }

    // Add the range around current page (excluding first and last if they're in range)
    for (let i = start; i <= end; i++) {
      if (i !== 1 && i !== totalPages) {
        rangeWithDots.push(i);
      }
    }

    // Add ellipsis before last page if needed
    if (end < totalPages - 1) {
      rangeWithDots.push('...');
    }

    // Always show last page (if it's not the first page)
    if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  if (totalPages <= 1) return null;

  return (
    <PaginationContainer>
      <PaginationInfo>
        Showing {startItem}-{endItem} of {totalItems} entries
      </PaginationInfo>
      
      <PaginationControls>
        <PaginationButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePageClick(currentPage - 1);
          }}
          disabled={currentPage === 1}
        >
          <ChevronLeft size={16} />
        </PaginationButton>

        {getVisiblePages().map((page, index) => (
          page === '...' ? (
            <span key={`dots-${index}`} style={{ padding: '0 0.5rem', color: '#666' }}>
              ...
            </span>
          ) : (
            <PaginationButton
              key={page}
              type="button"
              className={currentPage === page ? 'active' : ''}
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handlePageClick(page);
              }}
            >
              {page}
            </PaginationButton>
          )
        ))}

        <PaginationButton
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            handlePageClick(currentPage + 1);
          }}
          disabled={currentPage === totalPages}
        >
          <ChevronRight size={16} />
        </PaginationButton>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginLeft: '1rem' }}>
          <span style={{ fontSize: '0.9rem', color: '#666' }}>Go to:</span>
          <PageInput
            type="number"
            min="1"
            max={totalPages}
            value={currentPage}
            onChange={handlePageInputChange}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      </PaginationControls>
    </PaginationContainer>
  );
};

export default Pagination;